import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid } from '../Common';
import Tabs from '../Common/Tabs/Tabs';
import { StyledCellLink, Weight } from '../Common/DataGrid/CellComponents';
import { select_commonCellProps, text_commonCellProps, convertDateTime } from '../Common/DataGrid/utils';
import Routes from '../../routes';
import UserInfo from '../../UserInfo';
import { fetchFilterValues } from '../../utils';
import { REGULATIONS, UNMEASURED_REGULATIONS } from './regulationConfig';
import { TAB_NAMES, TABLE_LINES, CALC_GUIDES, getEmptyStateCopy } from './consolidationCopy';
import BandControl from './BandControl';

// The three cross-regulation consolidation tables under the applicability chart
// (AB#17146). Regulation leads every table; reg-variant values render per row for
// that row's regulation. Status chips read the phased tags — never a local calc.
// The Applicable Regulation cell is plain text this release: the regulation
// drill-down pages (17128/17135-17138) don't exist yet to link to.

const REGULATION_NAMES = Object.fromEntries(
    [...REGULATIONS, ...UNMEASURED_REGULATIONS].map((r) => [r.key, r.name])
);
const regulationName = (key) => REGULATION_NAMES[key] ?? key;

const BAND_COLORS = {
    Hit: { bg: '#FDECEA', fg: '#C62828' },
    Near: { bg: '#FFF4E5', fg: '#B45309' },
    Low: { bg: '#FFFDE7', fg: '#8D6E08' },
};

// The band selection persists per user like column choices (AB#17292), keyed the
// same way as the DataGrid's saved table state. Stored values are validated
// against the tab's band options, falling back to the Near + Hit default.
const BAND_DEFAULT = ['Near', 'Hit'];
const bandStorageKey = (tabKey) =>
    `compliance-center-${tabKey}/bands/${UserInfo.instanceId}/${UserInfo.userId}`;
const loadBands = (tabKey, options) => {
    try {
        const stored = JSON.parse(localStorage.getItem(bandStorageKey(tabKey)));
        if (Array.isArray(stored)) {
            const valid = stored.filter((b) => options.includes(b));
            if (valid.length > 0) return valid;
        }
    } catch { /* corrupt entry — fall back to the default */ }
    return BAND_DEFAULT.filter((b) => options.includes(b));
};

const StatusChip = ({ status }) => {
    const colors = BAND_COLORS[status] ?? { bg: '#EEEEEE', fg: '#424242' };
    return (
        <Chip
            label={status}
            size="small"
            sx={{ backgroundColor: colors.bg, color: colors.fg, fontWeight: 600, fontSize: '12px' }}
        />
    );
};

const fmtPercent = (value) =>
    value === null || value === undefined ? '' : `${parseFloat(value).toFixed(1)}%`;

// Projected Year-End % renders capped at ">300%" and is blank before day 90 (ADO 17075).
const fmtProjected = (value) =>
    value === null || value === undefined ? '' : value > 300 ? '>300%' : fmtPercent(value);

const dateLinkCell = (date, serviceEventId) => {
    if (!date) return '';
    const text = convertDateTime(date);
    if (!serviceEventId) return text;
    return (
        <StyledCellLink path={`/ServiceEvent/${serviceEventId}`} text={text} from="Compliance Center" />
    );
};

const assetLinkCell = (name, assetID) => (
    <StyledCellLink path={`/Inventory/${assetID}`} text={name} from="Compliance Center" primaryLink />
);

const locationLinkCell = (name, locationID) => (
    <StyledCellLink path={`/Location/${locationID}`} text={name} from="Compliance Center" primaryLink />
);

const inspectionColumns = [
    {
        field: 'regulation',
        headerName: 'Applicable Regulation',
        ...select_commonCellProps,
        renderCell: ({ row: { regulation } }) => regulationName(regulation),
    },
    {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        filterable: false,
        renderCell: ({ row: { status } }) => <StatusChip status={status} />,
    },
    {
        field: 'assetName',
        headerName: 'Asset Name',
        ...text_commonCellProps,
        renderCell: ({ row: { assetName, assetID } }) => assetLinkCell(assetName, assetID),
    },
    {
        field: 'lastInspectionDate',
        headerName: 'Last Inspection Date',
        flex: 1,
        filterable: false,
        renderCell: ({ row: { lastInspectionDate, leakInspectionServiceEventId } }) =>
            dateLinkCell(lastInspectionDate, leakInspectionServiceEventId),
    },
    {
        field: 'leakInspectionServiceEventId',
        headerName: 'Leak Inspection Service Event',
        flex: 1,
        filterable: false,
        renderCell: ({ row: { leakInspectionServiceEventId } }) =>
            leakInspectionServiceEventId ? (
                <StyledCellLink
                    path={`/ServiceEvent/${leakInspectionServiceEventId}`}
                    text={`#${leakInspectionServiceEventId}`}
                    from="Compliance Center"
                />
            ) : '',
    },
    {
        field: 'nextDue',
        headerName: 'Next Due',
        flex: 1,
        filterable: false,
        renderCell: ({ row: { nextDue } }) => (nextDue ? convertDateTime(nextDue) : ''),
    },
    { field: 'requiredCadence', headerName: 'Required Cadence', ...select_commonCellProps },
    { field: 'triggerBasis', headerName: 'Trigger Basis', flex: 1, filterable: false },
    { field: 'state', headerName: 'State', ...select_commonCellProps },
    {
        field: 'locationName',
        headerName: 'Location',
        ...select_commonCellProps,
        renderCell: ({ row: { locationName, locationID } }) => locationLinkCell(locationName, locationID),
    },
    { field: 'equipmentType', headerName: 'Equipment Type', ...select_commonCellProps },
    { field: 'tagNumber', headerName: 'Tag Number', ...text_commonCellProps, initiallyVisible: false },
    { field: 'assetID', headerName: 'Asset ID', flex: 1, filterable: false, initiallyVisible: false },
    { field: 'refrigerantType', headerName: 'Refrigerant Type', flex: 1, filterable: false, initiallyVisible: false },
    {
        field: 'fullCharge',
        headerName: 'Full Charge',
        flex: 1,
        filterable: false,
        initiallyVisible: false,
        renderCell: ({ row: { fullCharge } }) => <Weight value={fullCharge} />,
    },
    { field: 'monitoring', headerName: 'Monitoring', flex: 1, filterable: false, initiallyVisible: false },
    { field: 'regionDivision', headerName: 'Region/Division', ...select_commonCellProps, initiallyVisible: false },
    { field: 'systemManager', headerName: 'System Manager', ...select_commonCellProps, initiallyVisible: false },
];

const underRepairColumns = [
    {
        field: 'regulations',
        headerName: 'Applicable Regulation',
        ...select_commonCellProps,
        renderCell: ({ row: { regulations } }) => (regulations ?? []).map(regulationName).join(', '),
    },
    {
        field: 'assetName',
        headerName: 'System Name',
        ...text_commonCellProps,
        renderCell: ({ row: { assetName, assetID } }) => assetLinkCell(assetName, assetID),
    },
    {
        field: 'underRepairDate',
        headerName: 'Under Repair Date',
        flex: 1,
        filterable: false,
        renderCell: ({ row: { underRepairDate, underRepairServiceEventId } }) =>
            dateLinkCell(underRepairDate, underRepairServiceEventId),
    },
    { field: 'assetID', headerName: 'System Id', flex: 1, filterable: false },
    { field: 'instanceAssociationName', headerName: 'Region', ...select_commonCellProps },
    {
        field: 'locationName',
        headerName: 'Location',
        ...select_commonCellProps,
        renderCell: ({ row: { locationName, locationID } }) => locationLinkCell(locationName, locationID),
    },
    { field: 'locationID', headerName: 'Location Id', flex: 1, filterable: false },
    { field: 'assetType', headerName: 'System Type', ...select_commonCellProps },
    { field: 'materialType', headerName: 'Material Type', ...select_commonCellProps },
    {
        field: 'gasCapacityLbs',
        headerName: 'Gas Capacity',
        flex: 1,
        filterable: false,
        renderCell: ({ row: { gasCapacityLbs } }) => <Weight value={gasCapacityLbs} />,
    },
    {
        field: 'currentGasWeightLbs',
        headerName: 'Current Gas Weight',
        flex: 1,
        filterable: false,
        renderCell: ({ row: { currentGasWeightLbs } }) => <Weight value={currentGasWeightLbs} />,
    },
];

const chronicColumns = [
    {
        field: 'regulation',
        headerName: 'Applicable Regulation',
        ...select_commonCellProps,
        renderCell: ({ row: { regulation } }) => regulationName(regulation),
    },
    {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        filterable: false,
        renderCell: ({ row: { status } }) => <StatusChip status={status} />,
    },
    { field: 'calendarYear', headerName: 'Calendar Year', ...select_commonCellProps },
    {
        field: 'currentCyPercent',
        headerName: 'Current CY %',
        flex: 1,
        filterable: false,
        renderCell: ({ row: { currentCyPercent } }) => fmtPercent(currentCyPercent),
    },
    {
        field: 'assetName',
        headerName: 'Asset Name',
        ...text_commonCellProps,
        renderCell: ({ row: { assetName, assetID } }) => assetLinkCell(assetName, assetID),
    },
    {
        field: 'refrigerantAddedYtd',
        headerName: 'Refrigerant Added YTD',
        flex: 1,
        filterable: false,
        renderCell: ({ row: { refrigerantAddedYtd } }) => <Weight value={refrigerantAddedYtd} />,
    },
    {
        field: 'projectedYearEndPercent',
        headerName: 'Projected Year-End %',
        flex: 1,
        filterable: false,
        renderCell: ({ row: { projectedYearEndPercent } }) => fmtProjected(projectedYearEndPercent),
    },
    {
        field: 'threshold',
        headerName: 'Threshold (125%)',
        flex: 1,
        filterable: false,
        renderCell: () => '125%',
    },
    {
        field: 'locationName',
        headerName: 'Location',
        ...select_commonCellProps,
        renderCell: ({ row: { locationName, locationID } }) => locationLinkCell(locationName, locationID),
    },
    { field: 'equipmentType', headerName: 'Equipment Type', ...select_commonCellProps },
    {
        field: 'reportDeadline',
        headerName: 'Report Deadline',
        flex: 1,
        filterable: false,
        initiallyVisible: false,
        renderCell: ({ row: { reportDeadline } }) => (reportDeadline ? convertDateTime(reportDeadline) : ''),
    },
    { field: 'reportStatus', headerName: 'Report Status', flex: 1, filterable: false, initiallyVisible: false },
    { field: 'tagNumber', headerName: 'Tag Number', ...text_commonCellProps, initiallyVisible: false },
    { field: 'assetID', headerName: 'Asset ID', flex: 1, filterable: false, initiallyVisible: false },
    { field: 'refrigerantType', headerName: 'Refrigerant Type', flex: 1, filterable: false, initiallyVisible: false },
    {
        field: 'fullCharge',
        headerName: 'Full Charge',
        flex: 1,
        filterable: false,
        initiallyVisible: false,
        renderCell: ({ row: { fullCharge } }) => <Weight value={fullCharge} />,
    },
    { field: 'state', headerName: 'State', ...select_commonCellProps, initiallyVisible: false },
    { field: 'regionDivision', headerName: 'Region/Division', ...select_commonCellProps, initiallyVisible: false },
    { field: 'systemManager', headerName: 'System Manager', ...select_commonCellProps, initiallyVisible: false },
];

// One subtitle + calc-guide modal per tab, same affordance as the chart's (17116 AC8).
const TabHeading = ({ tabKey }) => {
    const [open, setOpen] = useState(false);
    return (
        <>
            <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 1 }}>
                {TABLE_LINES[tabKey]}
                <IconButton
                    onClick={() => setOpen(true)}
                    aria-label="How this table is calculated"
                    size="small"
                    sx={{ ':focus': { outline: 'none' }, ml: '2px' }}
                >
                    <InfoOutlinedIcon sx={{ fontSize: '16px' }} />
                </IconButton>
            </Typography>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ pr: 6 }}>
                    How this table is calculated
                    <IconButton
                        onClick={() => setOpen(false)}
                        aria-label="Close"
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {CALC_GUIDES[tabKey].map((section) => (
                        <Typography key={section.lead} sx={{ fontSize: '13px', mb: 1.5 }}>
                            <b><i>{section.lead}</i></b> {section.text}
                        </Typography>
                    ))}
                </DialogContent>
            </Dialog>
        </>
    );
};

const useDropdownFilters = (url) => {
    const [filters, setFilters] = useState([]);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const values = await fetchFilterValues(url);
                if (cancelled) return;
                // Regulation dropdowns come back as keys; show display names.
                setFilters(values.map((f) =>
                    f.property === 'regulation' || f.property === 'regulations'
                        ? { ...f, name: regulationName(f.value) }
                        : f));
            } catch {
                if (!cancelled) setFilters([]);
            }
        })();
        return () => { cancelled = true; };
    }, [url]);
    return filters;
};

// The page-level filter properties are distinct from the systemManager/regionDivision
// column fields: both can be in one request, and the server scopes TotalCount to the
// page filter only (AB#17291).
const pageFilterStatics = (pageFilter) => [
    ...(pageFilter?.systemManager ? [{ property: 'pageSystemManager', value: pageFilter.systemManager }] : []),
    ...(pageFilter?.regionDivision ? [{ property: 'pageRegionDivision', value: pageFilter.regionDivision }] : []),
];

const ConsolidationTable = ({ tabKey, url, exportUrl, dropdownUrl, columns, exportFileName, bandOptions, pageFilter }) => {
    const filters = useDropdownFilters(dropdownUrl);
    const [bands, setBands] = useState(() => (bandOptions ? loadBands(tabKey, bandOptions) : BAND_DEFAULT));
    const changeBands = (next) => {
        setBands(next);
        try {
            localStorage.setItem(bandStorageKey(tabKey), JSON.stringify(next));
        } catch { /* storage full/unavailable — selection still applies this session */ }
    };

    const staticFilters = useMemo(() => [
        ...pageFilterStatics(pageFilter),
        ...(bandOptions ? [{ property: 'bands', value: bands.join(',') }] : []),
    ], [pageFilter, bands, bandOptions]);

    return (
        <>
            <TabHeading tabKey={tabKey} />
            <DataGrid
                url={url}
                exportButtonURL={exportUrl}
                filterOptions={filters}
                columns={columns}
                tableId={`compliance-center-${tabKey}`}
                exportFileName={exportFileName}
                exportButtonAlt={`Export ${exportFileName}`}
                exportFileTypes={[{ type: 'csv', name: 'CSV' }, { type: 'xlsx', name: 'XLSX' }]}
                staticFilters={staticFilters}
                searchable
                exportVisibleColumns
                getEmptyState={getEmptyStateCopy(TAB_NAMES[tabKey])}
                toolbarLeftContent={bandOptions ? (
                    <BandControl options={bandOptions} bands={bands} onChange={changeBands} colors={BAND_COLORS} />
                ) : null}
            />
        </>
    );
};

const TAB_KEYS = ['inspection', 'underRepair', 'chronic'];

const ConsolidationTables = ({ pageFilter }) => {
    // The open tab round-trips through the URL hash (Common/Tabs pushes it), so
    // returning from a drill-down restores it (AC 10).
    const initialTab = (() => {
        const hash = parseInt(window.location.hash.replace('#', ''), 10);
        return hash >= 1 && hash <= 3 ? hash : 1;
    })();
    const [activeTab, setActiveTab] = useState(initialTab);

    return (
        <Box sx={{ mb: 2 }}>
            <Tabs
                activeTab={activeTab}
                onhandleChange={(e, value) => setActiveTab(value)}
                labels={TAB_KEYS.map((k) => TAB_NAMES[k])}
                tabElements={[
                    <ConsolidationTable
                        tabKey="inspection"
                        url={Routes.ComplianceCenter.AssetsDueForInspection(false)}
                        exportUrl={Routes.ComplianceCenter.AssetsDueForInspection(true)}
                        dropdownUrl={Routes.ComplianceCenter.AssetsDueForInspectionDropdown()}
                        columns={inspectionColumns}
                        exportFileName="Assets Due for Inspection"
                        bandOptions={['Near', 'Hit']}
                        pageFilter={pageFilter}
                    />,
                    <ConsolidationTable
                        tabKey="underRepair"
                        url={Routes.ComplianceCenter.UnderRepairSystems(false)}
                        exportUrl={Routes.ComplianceCenter.UnderRepairSystems(true)}
                        dropdownUrl={Routes.ComplianceCenter.UnderRepairSystemsDropdown()}
                        columns={underRepairColumns}
                        exportFileName="Under Repair Systems"
                        bandOptions={null}
                        pageFilter={pageFilter}
                    />,
                    <ConsolidationTable
                        tabKey="chronic"
                        url={Routes.ComplianceCenter.ChronicallyLeaking(false)}
                        exportUrl={Routes.ComplianceCenter.ChronicallyLeaking(true)}
                        dropdownUrl={Routes.ComplianceCenter.ChronicallyLeakingDropdown()}
                        columns={chronicColumns}
                        exportFileName="Chronically Leaking Systems"
                        bandOptions={['Low', 'Near', 'Hit']}
                        pageFilter={pageFilter}
                    />,
                ]}
            />
        </Box>
    );
};

export default ConsolidationTables;
